import { QueryCache, MutationCache, QueryClient } from "@tanstack/react-query";
import { createRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { routeTree } from "./routeTree.gen";

export const getRouter = () => {
  const isNoiseError = (error: unknown, key: unknown) => {
    const name = (error as { name?: string })?.name;
    if (name === "AbortError" || name === "CanceledError") return true;
    const msg = String((error as { message?: string })?.message ?? "");
    // Sign-out / unauthenticated refetches after logout are expected, not bugs.
    if (/Unauthorized|No authorization header|401/i.test(msg)) return true;
    // Auth/session probe queries: harmless when signed out.
    const k = Array.isArray(key) ? key.join(":") : String(key ?? "");
    if (k === "admin:me" || k.startsWith("admin:error-log")) return true;
    return false;
  };

  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (typeof window === "undefined") return;
        if (isNoiseError(error, query.queryKey)) return;
        void import("@/lib/client-errors/tracker").then((m) =>
          m.reportClientError(error, {
            source: "query",
            action: String(query.queryKey?.[0] ?? "query"),
            context: { queryKey: JSON.stringify(query.queryKey).slice(0, 300) },
          }),
        );
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        if (typeof window === "undefined") return;
        if (isNoiseError(error, mutation.options.mutationKey)) return;
        void import("@/lib/client-errors/tracker").then((m) =>
          m.reportClientError(error, {
            source: "mutation",
            action: String(mutation.options.mutationKey?.[0] ?? "mutation"),
          }),
        );
      },
    }),
  });


  const router = createRouter({
    routeTree,
    context: { queryClient },
    scrollRestoration: true,
    defaultPreloadStaleTime: 0,
  });

  setupRouterSsrQueryIntegration({ router, queryClient });
  return router;
};
